import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { cn } from "@/lib/utils";

const nav = [
  { to: "/", label: "ホーム", end: true },
  { to: "/app", label: "アプリ" },
  { to: "/layout", label: "レイアウト解析" },
  { to: "/restore", label: "復元" },
  { to: "/ai", label: "AI 編集" },
  { to: "/about", label: "About" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-30 border-b border-[var(--color-border)] bg-[var(--color-bg)]/85 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:h-16 sm:px-6">
        <NavLink to="/" className="flex min-w-0 items-center gap-2">
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-coral)"
            strokeWidth="2"
            aria-label="PPTX JSON Studio logo"
            className="shrink-0"
          >
            <title>PPTX JSON Studio</title>
            <rect x="3" y="3" width="18" height="18" rx="4" />
            <path d="M8 10h8M8 14h5" />
          </svg>
          <span className="truncate font-display text-base font-medium tracking-tight sm:text-lg">
            PPTX JSON Studio
          </span>
        </NavLink>

        <nav className="hidden items-center gap-1 md:flex">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "rounded-[10px] px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-[var(--color-surface-muted)] text-[var(--color-ink)]"
                    : "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          <ThemeToggle />
        </nav>

        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "メニューを閉じる" : "メニューを開く"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] text-[var(--color-ink)] hover:bg-[var(--color-surface-muted)]"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <>
          <button
            type="button"
            aria-label="メニューを閉じる"
            onClick={() => setOpen(false)}
            className="fixed inset-0 top-14 z-20 bg-[var(--color-ink)]/30 backdrop-blur-[2px] md:hidden"
          />
          <nav
            id="mobile-nav"
            className="relative z-30 border-t border-[var(--color-border)] bg-[var(--color-bg)] md:hidden"
          >
            <ul className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
              {nav.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center rounded-[10px] px-4 py-3 text-base transition-colors",
                        isActive
                          ? "bg-[var(--color-coral-soft)]/40 font-medium text-[var(--color-coral-dark)]"
                          : "text-[var(--color-ink)] hover:bg-[var(--color-surface-muted)]",
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </>
      )}
    </header>
  );
}
